import { SectionSelectField } from "@/components/SectionSelectField";
import { saveErrorMessage } from "@/lib/saveErrorMessage";
import { useState, useMemo, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Clock, Bed } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { z } from "zod";
import { Session } from "@supabase/supabase-js";
import { format, differenceInDays } from "date-fns";
import { AddCategoryDialogInline } from "./AddCategoryDialogInline";
import { AddSupplierDialog } from "./AddSupplierDialog";
import { AddProductDialogInline } from "./AddProductDialogInline";
import { AddCustomItemDialog } from "./AddCustomItemDialog";
import { DatePickerField } from "./DatePickerField";
import { SessionScheduleField } from "./SessionScheduleField";
import { DEFAULT_SCHEDULE, scheduleToPayload, type SessionSchedule } from "@/lib/sessionSchedule";
import { Sparkles } from "lucide-react";
import { calculatePrice, PricingTier } from "@/lib/pricingUtils";
import { useDatePickerDefault } from "@/hooks/itinerary-dates";
import { ItemInclusionsExclusionsFields, arrayToLines, linesToArray } from "./ItemInclusionsExclusionsFields";
import { DEFAULT_ITINERARY_SECTIONS, defaultSectionForItem, parseItinerarySections } from "@/lib/itinerarySections";


const itemSchema = z.object({
  item_type: z.enum(["accommodation", "transfer", "activity", "meal", "flight", "language_excursions", "conference", "tour", "insurance", "supplier", "universal_fee"]),
  date: z.date().optional(),
  check_in: z.date().optional(),
  check_out: z.date().optional(),
  service_start: z.date().optional(),
  service_end: z.date().optional(),
  accommodation_type: z.string().optional(),
  location: z.string().trim().max(200, "Location must be less than 200 characters").optional(),
  cost: z.number().min(0, "Cost must be positive").optional(),
  quantity: z.number().int().min(1, "Quantity must be at least 1").optional(),
  supplier: z.string().trim().max(200, "Supplier must be less than 200 characters").optional(),
  description: z.string().trim().max(2500, "Description must be less than 2500 characters").optional(),
  notes: z.string().trim().max(2500, "Notes must be less than 2500 characters").optional(),
 universal_fee_kind: z.enum(["portage", "guiding", "assistant_guide", "st", "staff_hotel", "chef", "fejo", "custom"]).optional(),
  staff_categories: z.array(z.string()).optional(),
});

interface ItineraryBuilderProps {
  leadId: string;
  session: Session | null;
  leadStatus?: string;
  leadStartDate?: string | null;
  paxCount?: number;
  leadCountries?: string[] | null;
  leadDestination?: string | null;
  isMultiCountry?: boolean | null;
}

export const ItineraryBuilder = ({ leadId, session, leadStatus, leadStartDate, paxCount, leadCountries, leadDestination, isMultiCountry }: ItineraryBuilderProps) => {
  const isCancelled = leadStatus === 'cancelled';
  const isReadOnly = leadStatus === 'quote_accepted';
  const [open, setOpen] = useState(false);
  const [customItemOpen, setCustomItemOpen] = useState(false);
  const queryClient = useQueryClient();
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Single source of truth for the itinerary default date.
  const defaultDate = useDatePickerDefault(leadStartDate);
  // Tracks whether the user has manually picked any date in this dialog.
  // The async sync effect must only adopt the hook value when this is false.
  // Do NOT use field === undefined as a proxy — that caused the original regression.
  const userHasPicked = useRef(false);
  
  // Location filter state
  const [selectedLocation, setSelectedLocation] = useState<string>("");

  // Countries associated with this lead (from lead-level setup)
  const leadCountryList = useMemo(() => {
    const list = (leadCountries || []).filter(Boolean);
    if (list.length > 0) return list;
    return leadDestination ? [leadDestination] : [];
  }, [leadCountries, leadDestination]);

  // A single-country lead inherits its country: itinerary items must not ask for it
  const inheritedCountry = useMemo(() => {
    if (isMultiCountry) return "";
    return leadCountryList.length === 1 ? leadCountryList[0] : "";
  }, [isMultiCountry, leadCountryList]);

  // Seed the location filter from the lead when the dialog opens
  useEffect(() => {
    if (open && inheritedCountry) {
      setSelectedLocation(inheritedCountry);
      setFormData((prev) => ({ ...prev, location: prev.location || inheritedCountry }));
    }
  }, [open, inheritedCountry]);

  // Fetch countries for location dropdown
  const { data: countries } = useQuery({
    queryKey: ["location-options-countries"],
    queryFn: async () => {
      const { data, error } = await supabase.from("countries").select("id, name").order("name");
      if (error) throw error;
      return data || [];
    },
  });

  // Multi-country leads only choose between the lead's own countries
  const countryOptions = useMemo(() => {
    if (!countries) return [];
    if (isMultiCountry && leadCountryList.length > 0) {
      return countries.filter((c) => leadCountryList.includes(c.name));
    }
    return countries;
  }, [countries, isMultiCountry, leadCountryList]);

  // When a country is selected, fetch its cities and parks for product filtering
  const { data: countryLocations } = useQuery({
    queryKey: ["country-locations", selectedLocation],
    queryFn: async () => {
      const country = countries?.find(c => c.name === selectedLocation);
      if (!country) return [];
      const [citiesRes, parksRes] = await Promise.all([
        supabase.from("cities").select("name").eq("country_id", country.id),
        supabase.from("parks").select("name").eq("country_id", country.id),
      ]);
      return [
        ...(citiesRes.data || []).map(c => c.name),
        ...(parksRes.data || []).map(p => p.name),
      ];
    },
    enabled: !!selectedLocation && !!countries,
  });

  // Track selected product's pricing info for dynamic recalculation
  const [selectedProductPricing, setSelectedProductPricing] = useState<{
    tour_type: string | null;
    base_price: number | null;
    pricing_tiers: PricingTier[] | null;
  } | null>(null);
  
  // Track available time slots and selection
  const [availableTimeSlots, setAvailableTimeSlots] = useState<Array<{label: string; start: string; end: string}>>([]);
  const [selectedTimeSlotIndex, setSelectedTimeSlotIndex] = useState<number | null>(null);
  
  // Custom time state
  const [useCustomTime, setUseCustomTime] = useState(false);
  const [customStartTime, setCustomStartTime] = useState("");
  const [customEndTime, setCustomEndTime] = useState("");
  
  // Track selected supplier for room rates query (accommodation)
  const [selectedSupplierId, setSelectedSupplierId] = useState<string | null>(null);
  const [selectedRoomRateId, setSelectedRoomRateId] = useState<string | null>(null);
  const [occupancyType, setOccupancyType] = useState<"single" | "double" | "triple">("double");
  const [roomCount, setRoomCount] = useState(1);
  const [guestCount, setGuestCount] = useState<number>(paxCount || 1);

  // Reset guest count when dialog opens or pax changes
  useEffect(() => {
    if (open) setGuestCount(paxCount || 1);
  }, [open, paxCount]);
  
  // Helper function to get per-person rate based on quantity and pricing tier
  const getPerPersonRate = (pricing: typeof selectedProductPricing, quantity: number): number => {
    if (!pricing) return 0;
    
    if (pricing.tour_type === 'private' && pricing.pricing_tiers?.length) {
      const tier = pricing.pricing_tiers.find(
        t => quantity >= t.min_pax && quantity <= t.max_pax
      );
      return tier?.price_per_person || pricing.base_price || 0;
    }
    return pricing.base_price || 0;
  };
  
  const [formData, setFormData] = useState<{
    item_type: "accommodation" | "transfer" | "activity" | "meal" | "flight" | "language_excursions" | "conference" | "tour" | "insurance" | "supplier" | "universal_fee";
    date: Date | undefined;
    check_in: Date | undefined;
    check_out: Date | undefined;
    service_start: Date | undefined;
    service_end: Date | undefined;
    accommodation_type: string;
    location: string;
    cost: string;
    quantity: string;
    supplier: string;
    product_id: string;
    product_name: string;
    description: string;
    inclusions: string;
    exclusions: string;

    notes: string;
    universal_fee_kind: "" | "portage" | "guiding" | "assistant_guide" | "st" | "staff_hotel" | "chef" | "fejo" | "custom";
    custom_fee_label: string;
    staff_categories: string[];
    section_name: string;
  }>({
    item_type: "accommodation",
    date: defaultDate,
    check_in: defaultDate,
    check_out: defaultDate,
    service_start: defaultDate,
    service_end: defaultDate,
    accommodation_type: "",
    location: "",
    cost: "",
    quantity: "1",
    supplier: "",
    product_id: "",
    product_name: "",
    description: "",
    inclusions: "",
    exclusions: "",

    notes: "",
    universal_fee_kind: "",
    custom_fee_label: "",
    staff_categories: [],
    section_name: "",
  });

  const { data: layoutSettings } = useQuery({
    queryKey: ["lead-itinerary-layout", leadId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("leads")
        .select("itinerary_layout, itinerary_sections")
        .eq("id", leadId)
        .single();
      if (error) throw error;
      return data;
    },
  });
  const sectionOptions = [...new Set([
    ...parseItinerarySections(layoutSettings?.itinerary_sections),
    ...DEFAULT_ITINERARY_SECTIONS,
  ])];

  const [savingDefaults, setSavingDefaults] = useState(false);

  // Save the current inclusion/exclusion lines back onto the linked experience or supplier
  const handleSaveDefaults = async () => {
    const inc = linesToArray(formData.inclusions);
    const exc = linesToArray(formData.exclusions);
    const payload = {
      default_inclusions: inc.length ? inc : null,
      default_exclusions: exc.length ? exc : null,
    };
    setSavingDefaults(true);
    try {
      if (formData.product_id) {
        const { error } = await supabase.from("products").update(payload).eq("id", formData.product_id);
        if (error) throw error;
      } else if (formData.supplier?.trim()) {
        const { data: match, error: findError } = await supabase
          .from("suppliers")
          .select("id")
          .ilike("name", formData.supplier.trim())
          .limit(1)
          .maybeSingle();
        if (findError) throw findError;
        if (!match) {
          toast.error("Couldn't find that supplier to save the defaults to");
          return;
        }
        const { error } = await supabase.from("suppliers").update(payload).eq("id", match.id);
        if (error) throw error;
      } else {
        return;
      }
      toast.success("Saved as the default inclusions and exclusions");
    } catch (e: any) {
      toast.error(e?.message || "Could not save the defaults");
    } finally {
      setSavingDefaults(false);
    }
  };



  // Adopt the hook value when leadStartDate resolves after the lead loads,
  // unless the user has already picked a date themselves.
  useEffect(() => {
    if (userHasPicked.current) return;
    if (!defaultDate) return;
    setFormData((prev) => ({
      ...prev,
      date: prev.date ?? defaultDate,
      check_in: prev.check_in ?? defaultDate,
      check_out: prev.check_out ?? defaultDate,
    }));
  }, [defaultDate]);

  // Reset the "user picked" flag whenever the dialog closes.
  useEffect(() => {
    if (!open) userHasPicked.current = false;
  }, [open]);

  // Per-category cost inputs used when fee kind = staff_hotel
  const [staffHotelCosts, setStaffHotelCosts] = useState<Record<string, string>>({});
  // Supplier category chosen as an item type (categories created in the OS,
  // e.g. Education). Stored on the item; behaves like an Activity.
  const [schedule, setSchedule] = useState<SessionSchedule>({ ...DEFAULT_SCHEDULE });
  const [customCategoryId, setCustomCategoryId] = useState<string | null>(null);

  // Fetch supplier categories
  const { data: categories } = useQuery({
    queryKey: ["supplier-categories"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("supplier_categories")
        .select("*")
        .order("name");
      if (error) throw error;
      return data;
    },
  });

  const BUILT_IN_CATEGORY_NAMES = new Set([
    "accommodation", "hotels", "guesthouses", "safari lodge", "beach lodges", "hostels",
    "retreat lodge", "villas", "transport", "activity", "restaurants", "airlines",
    "language excursions", "insurance", "tour", "conference",
  ]);
  const extraCategories = (categories || []).filter(
    (c: any) => !c.parent_category_id && !BUILT_IN_CATEGORY_NAMES.has(String(c.name).trim().toLowerCase())
  );
  const customCategory = customCategoryId
    ? (categories || []).find((c: any) => c.id === customCategoryId) || null
    : null;

  // Fetch suppliers based on item type, filtered by location
  const { data: suppliers } = useQuery({
    queryKey: ["suppliers-by-item-type", formData.item_type, formData.accommodation_type, selectedLocation, customCategoryId],
    queryFn: async () => {
      // Map item types to supplier categories
      const itemTypeToCategoryMap: Record<string, string> = {
        "accommodation": formData.accommodation_type ? {
          "hotels": "Hotels",
          "guesthouses": "Guesthouses",
          "safari_lodges": "Safari Lodge",
          "beach_lodges": "Beach Lodges",
          "hostels": "Hostels",
          "retreat_lodges": "Retreat Lodge",
          "villas": "Villas",
        }[formData.accommodation_type] || "" : "",
        "transfer": "Transport",
        "activity": "Activity",
        "meal": "Restaurants",
        "flight": "Airlines",
        "language_excursions": "Language Excursions",
        "conference": "Conference",
        "insurance": "Insurance",
      };
      
      const categoryName = itemTypeToCategoryMap[formData.item_type];
      const category = customCategory || categories?.find(c => c.name === categoryName);
      if (!category) return [];
      
      let query = supabase
        .from("suppliers")
        .select("*, description")
        .eq("category_id", category.id)
        .order("name");

      if (selectedLocation) {
        query = query.ilike("territory", `%${selectedLocation}%`);
      }
      
      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
    enabled: !!categories && formData.item_type !== "tour" && (formData.item_type !== "accommodation" || !!formData.accommodation_type),
  });

  // Fetch ALL suppliers when in "supplier" item type mode, filtered by country if selected
  const { data: allSuppliers } = useQuery({
    queryKey: ["all-suppliers-for-itinerary", selectedLocation],
    queryFn: async () => {
      let query = supabase
        .from("suppliers")
        .select("id, name, city, territory, description, product_name, default_inclusions, default_exclusions")
        .order("name");
      if (selectedLocation) {
        query = query.ilike("territory", `%${selectedLocation}%`);
      }
      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    },
    enabled: formData.item_type === "supplier",
  });

  // Fetch room rates for selected accommodation supplier
  const { data: supplierRoomRates } = useQuery({
    queryKey: ["supplier-room-rates", selectedSupplierId],
    queryFn: async () => {
      if (!selectedSupplierId) return [];
      const { data, error } = await supabase
        .from("supplier_room_rates")
        .select("*")
        .eq("supplier_id", selectedSupplierId)
        .eq("is_active", true)
        .order("room_type");
      if (error) throw error;
      return data;
    },
    enabled: !!selectedSupplierId && formData.item_type === "accommodation",
  });

  const isVilla = formData.item_type === "accommodation" && formData.accommodation_type === "villas";

  // For villas, auto-select the (single) Private Hire rate and use single_rate as flat nightly cost.
  useEffect(() => {
    if (!isVilla) return;
    if (!supplierRoomRates || supplierRoomRates.length === 0) return;
    if (selectedRoomRateId) return;
    const rate = supplierRoomRates[0];
    setSelectedRoomRateId(rate.id);
    setOccupancyType("single");
    setRoomCount(1);
    setFormData((prev) => ({
      ...prev,
      cost: (rate.single_rate ?? rate.double_rate ?? 0).toString(),
    }));
  }, [isVilla, supplierRoomRates, selectedRoomRateId]);

  // Calculate number of nights for accommodation
  const numberOfNights = useMemo(() => {
    if (!formData.check_in || !formData.check_out) return 0;
    // Normalize to start of day to ensure accurate night count
    const checkInDate = new Date(formData.check_in);
    const checkOutDate = new Date(formData.check_out);
    checkInDate.setHours(0, 0, 0, 0);
    checkOutDate.setHours(0, 0, 0, 0);
    return differenceInDays(checkOutDate, checkInDate);
  }, [formData.check_in, formData.check_out]);

  // Get rate based on occupancy type
  const getRateByOccupancy = (roomRate: any, occType: "single" | "double" | "triple"): number => {
    if (!roomRate) return 0;
    switch (occType) {
      case "single": return roomRate.single_rate || 0;
      case "triple": return roomRate.triple_rate || roomRate.double_rate || 0;
      default: return roomRate.double_rate || 0;
    }
  };

  // Fetch products based on item type and category, filtered by location
  const { data: products } = useQuery({
    queryKey: ["products-by-item-type", formData.item_type, formData.accommodation_type, selectedLocation, countryLocations, customCategoryId],
    queryFn: async () => {
      // Map item types to supplier categories
      const itemTypeToCategoryMap: Record<string, string> = {
        "accommodation": formData.accommodation_type ? {
          "hotels": "Hotels",
          "guesthouses": "Guesthouses",
          "safari_lodges": "Safari Lodge",
          "beach_lodges": "Beach Lodges",
          "hostels": "Hostels",
          "retreat_lodges": "Retreat Lodge",
          "villas": "Villas",
        }[formData.accommodation_type] || "" : "",
      "transfer": "Transport",
      "activity": "Activity",
      "meal": "Restaurants",
      "flight": "Airlines",
      "tour": "Tour",
      "language_excursions": "Language Excursions",
      "conference": "Conference",
      "insurance": "Insurance",
      };
      
      const categoryName = itemTypeToCategoryMap[formData.item_type];
      const category = customCategory || categories?.find(c => c.name === categoryName);
      if (!category) return [];
      
      let query = supabase
        .from("products")
        .select("id, product_name, description, city, supplier_id, tour_type, base_price, pricing_tiers, time_slots, default_inclusions, default_exclusions, suppliers(id, name)")
        .eq("category_id", category.id)
        .order("product_name");

      if (selectedLocation && countryLocations?.length) {
        query = query.in("city", countryLocations);
      }
      
      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    },
    enabled: !!categories && (formData.item_type !== "accommodation" || !!formData.accommodation_type),
  });

  const createItemMutation = useMutation({
    mutationFn: async (data: any) => {
      // Calculate next sort_order to place new items at the bottom
      const { data: maxOrderData } = await supabase
        .from("itinerary_items")
        .select("sort_order")
        .eq("lead_id", leadId)
        .order("sort_order", { ascending: false, nullsFirst: false })
        .limit(1)
        .single();
      
      const nextSortOrder = (maxOrderData?.sort_order ?? -1) + 1;
      
      const payloads = Array.isArray(data) ? data : [data];
      const rows = payloads.map((p, i) => ({
        ...p,
        sort_order: nextSortOrder + i,
      }));
      const { error } = await supabase.from("itinerary_items").insert(rows);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["itinerary-items", leadId] });
      toast.success("Added to itinerary!");
      setOpen(false);
      userHasPicked.current = false;
      setFormData({
        item_type: "accommodation",
        date: defaultDate,
        check_in: defaultDate,
        check_out: defaultDate,
        service_start: defaultDate,
        service_end: defaultDate,
        accommodation_type: "",
        location: "",
        cost: "",
        quantity: "1",
        supplier: "",
        product_id: "",
        product_name: "",
        description: "",
        inclusions: "",
        exclusions: "",

        notes: "",
        universal_fee_kind: "",
        custom_fee_label: "",
        staff_categories: [],
        section_name: "",
      });
      setStaffHotelCosts({});
      // Reset time slot state
      setAvailableTimeSlots([]);
      setSelectedTimeSlotIndex(null);
      setUseCustomTime(false);
      setCustomStartTime("");
      setCustomEndTime("");
      // Reset room rate state
      setSelectedSupplierId(null);
      setSelectedRoomRateId(null);
      setOccupancyType("double");
      setRoomCount(1);
      setSelectedLocation("");
      setErrors({});
    },
    onError: (error) => {
      toast.error(saveErrorMessage(error, "Failed to add item"));
      console.error(error);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    if (!session?.user?.id) {
      toast.error("You must be logged in to add items");
      return;
    }

    try {
      const validatedData = itemSchema.parse({
        item_type: formData.item_type,
        date: formData.date,
        check_in: formData.check_in,
        check_out: formData.check_out,
        service_start: formData.service_start,
        service_end: formData.service_end,
        accommodation_type: formData.accommodation_type || undefined,
        location: formData.location || undefined,
        cost: formData.cost ? parseFloat(formData.cost) : undefined,
        quantity: formData.quantity ? parseInt(formData.quantity) : 1,
        supplier: formData.supplier || undefined,
        description: formData.description || undefined,
        notes: formData.notes || undefined,
        universal_fee_kind: formData.universal_fee_kind || undefined,
        staff_categories: formData.staff_categories.length ? formData.staff_categories : undefined,
      });

      // Extra validation for universal_fee
      if (formData.item_type === "universal_fee") {
        if (!formData.universal_fee_kind) {
          setErrors({ universal_fee_kind: "Please select a fee kind" });
          toast.error("Please select a fee kind");
          return;
        }
        if (formData.universal_fee_kind === "custom" && !formData.custom_fee_label.trim()) {
          setErrors({ custom_fee_label: "Please name this fee" });
          toast.error("Please name this custom fee");
          return;
        }
        if (
          (formData.universal_fee_kind === "st" || formData.universal_fee_kind === "staff_hotel") &&
          formData.staff_categories.length === 0
        ) {
          setErrors({ staff_categories: "Select at least one staff category" });
          toast.error("Select at least one staff category");
          return;
        }
        if (formData.universal_fee_kind === "staff_hotel") {
          const missing = formData.staff_categories.find(
            (c) => !staffHotelCosts[c] || parseFloat(staffHotelCosts[c]) <= 0
          );
          if (missing) {
            setErrors({ staff_categories: `Enter a cost for ${missing}` });
            toast.error(`Enter a cost for ${missing}`);
            return;
          }
        }
        // Multi-day kinds need a service window with end >= start
        if (
          formData.universal_fee_kind === "guiding" || formData.universal_fee_kind === "assistant_guide" ||
          formData.universal_fee_kind === "st" ||
          formData.universal_fee_kind === "chef"
        ) {
          if (!formData.service_start || !formData.service_end) {
            setErrors({ service_start: "Start and end date are required" });
            toast.error("Please pick a start and end date");
            return;
          }
          if (formData.service_end < formData.service_start) {
            setErrors({ service_end: "End date must be on or after start date" });
            toast.error("End date must be on or after start date");
            return;
          }
        }
      }

      // Generate title based on product name first, then supplier, then fallback
      const generatedTitle = formData.product_name || formData.supplier || 
        (customCategory ? customCategory.name :
         formData.item_type === 'accommodation' ? 'Accommodation' :
         formData.item_type === 'transfer' ? 'Transfer' :
         formData.item_type === 'activity' ? 'Activity' :
         formData.item_type === 'meal' ? 'Meal' :
         formData.item_type === 'flight' ? 'Flight' :
         formData.item_type === 'language_excursions' ? 'Language Excursion' : formData.item_type === 'conference' ? 'Conference' :
         formData.item_type === 'insurance' ? 'Insurance' :
         formData.item_type === 'supplier' ? (formData.supplier || 'Supplier') :
         formData.item_type === 'universal_fee' ? (
           formData.universal_fee_kind === 'portage' ? 'Portage Fees' :
           formData.universal_fee_kind === 'guiding' ? 'Guiding Fees' :
           formData.universal_fee_kind === 'assistant_guide' ? 'Assistant Guide Fees' :
           formData.universal_fee_kind === 'st' ? 'S&T' :
           formData.universal_fee_kind === 'chef' ? 'Private Chef' :
            formData.universal_fee_kind === 'fejo' ? 'FEJO Consulting Fee' :
            formData.universal_fee_kind === 'custom' ? (formData.custom_fee_label.trim() || 'Custom Fee') :
            formData.universal_fee_kind === 'staff_hotel'
              ? `Staff Hotel${formData.staff_categories[0] ? ' — ' + formData.staff_categories[0].charAt(0).toUpperCase() + formData.staff_categories[0].slice(1) : ''}`
              : 'Universal Fee'
         ) :
         'Tour');

      // Get the selected time slot — custom time takes priority over predefined slots
      const selectedTimeSlot = (useCustomTime || availableTimeSlots.length === 0) && customStartTime
        ? { label: "Custom", start: customStartTime, end: customEndTime || customStartTime }
        : (selectedTimeSlotIndex !== null && availableTimeSlots[selectedTimeSlotIndex]
            ? availableTimeSlots[selectedTimeSlotIndex]
            : null);

      // For accommodation items, use check_in as the primary date for proper sorting.
      // Guide / S&T universal fees are programme-wide and intentionally have no date.
      const isAccommodation = formData.item_type === 'accommodation';
      const isInfraFee =
        formData.item_type === 'universal_fee' &&
        (formData.universal_fee_kind === 'guiding' || formData.universal_fee_kind === 'assistant_guide' ||
          formData.universal_fee_kind === 'st' ||
          formData.universal_fee_kind === 'chef' ||
          formData.universal_fee_kind === 'fejo');
      const primaryDate = isInfraFee
        ? null
        : isAccommodation && validatedData.check_in
          ? format(validatedData.check_in, 'yyyy-MM-dd')
          : (validatedData.date ? format(validatedData.date, 'yyyy-MM-dd') : null);

      // For accommodation, use roomCount as quantity
      const finalQuantity = isAccommodation
        ? (isVilla ? guestCount : roomCount)
        : (formData.item_type === 'universal_fee' && formData.universal_fee_kind === 'chef'
            ? 1
            : (validatedData.quantity || 1));

      const inclusionLines = linesToArray(formData.inclusions);
      const exclusionLines = linesToArray(formData.exclusions);

      const basePayload = {
        ...validatedData,
        inclusions: inclusionLines.length ? inclusionLines : null,
        exclusions: exclusionLines.length ? exclusionLines : null,

        title: generatedTitle,
        date: primaryDate,
        check_in: validatedData.check_in ? format(validatedData.check_in, 'yyyy-MM-dd') : null,
        check_out: validatedData.check_out ? format(validatedData.check_out, 'yyyy-MM-dd') : null,
        service_start: isInfraFee && validatedData.service_start
          ? format(validatedData.service_start, 'yyyy-MM-dd')
          : null,
        service_end: isInfraFee && validatedData.service_end
          ? format(validatedData.service_end, 'yyyy-MM-dd')
          : null,
        lead_id: leadId,
        user_id: session.user.id,
        cost: validatedData.cost || 0,
        quantity: finalQuantity,
        selected_time_slot: selectedTimeSlot,
        occupancy_type: isAccommodation ? occupancyType : null,
        room_rate_id: isAccommodation ? selectedRoomRateId : null,
        product_id: formData.product_id || null,
        category_id: customCategoryId,
        section_name: formData.section_name.trim() || defaultSectionForItem({ item_type: formData.item_type } as any),
        ...(!isAccommodation && formData.item_type !== 'universal_fee' ? scheduleToPayload(schedule, validatedData.date) : {}),
        universal_fee_kind: formData.item_type === 'universal_fee' ? formData.universal_fee_kind : null,
        custom_title: formData.item_type === 'universal_fee' && formData.universal_fee_kind === 'custom'
          ? formData.custom_fee_label.trim()
          : undefined,
        staff_categories: formData.item_type === 'universal_fee' &&
          (formData.universal_fee_kind === 'st' || formData.universal_fee_kind === 'staff_hotel')
          ? formData.staff_categories
          : null,
      };

      // Staff Hotel splits into one row per selected category with its own cost
      if (
        formData.item_type === 'universal_fee' &&
        formData.universal_fee_kind === 'staff_hotel'
      ) {
        const rows = formData.staff_categories.map((cat) => ({
          ...basePayload,
          cost: parseFloat(staffHotelCosts[cat]) || 0,
          staff_categories: [cat],
          title: `Staff Hotel — ${cat.charAt(0).toUpperCase() + cat.slice(1)}`,
        }));
        createItemMutation.mutate(rows as any);
      } else {
        createItemMutation.mutate(basePayload);
      }
    } catch (error) {
      if (error instanceof z.ZodError) {
        const newErrors: Record<string, string> = {};
        error.errors.forEach((err) => {
          if (err.path[0]) {
            newErrors[err.path[0].toString()] = err.message;
          }
        });
        setErrors(newErrors);
        toast.error("Please fix the validation errors");
      }
    }
  };

  const isAccommodation = formData.item_type === "accommodation";
  const isTour = formData.item_type === "tour";
  const isSupplierType = formData.item_type === "supplier";
  const isUniversalFee = formData.item_type === "universal_fee";
  const universalKind = formData.universal_fee_kind;
  const isDayBased = isUniversalFee && (universalKind === "guiding" || universalKind === "assistant_guide" || universalKind === "st" || universalKind === "staff_hotel" || universalKind === "chef");
  const isInfrastructureFeeKind = isUniversalFee && (universalKind === "guiding" || universalKind === "assistant_guide" || universalKind === "st" || universalKind === "chef" || universalKind === "fejo");
  const isMultiDayService = isUniversalFee && (universalKind === "guiding" || universalKind === "assistant_guide" || universalKind === "st" || universalKind === "chef");

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2" disabled={isCancelled || isReadOnly}>
              <Plus className="h-4 w-4" />
              Add Item
            </Button>
          </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Itinerary Item</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {layoutSettings?.itinerary_layout === "sections" && (
            <SectionSelectField
              id="new_item_section"
              leadId={leadId}
              value={formData.section_name}
              onChange={(v) => setFormData((prev: any) => ({ ...prev, section_name: v }))}
              options={sectionOptions}
            />
          )}
          <div className="space-y-2">
            <Label htmlFor="item_type">Item Type *</Label>
            <Select
              value={customCategoryId ? `cat:${customCategoryId}` : formData.item_type}
              onValueChange={(raw: any) => {
                const isCat = typeof raw === "string" && raw.startsWith("cat:");
                const value = isCat ? "activity" : raw;
                setCustomCategoryId(isCat ? raw.slice(4) : null);
                setFormData({ ...formData, item_type: value, accommodation_type: "", supplier: "", product_id: "", product_name: "", location: "" });
                // Reset room rate state when changing item type
                setSelectedSupplierId(null);
                setSelectedRoomRateId(null);
                setOccupancyType("double");
                setSelectedLocation("");
              }}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="accommodation">Accommodation</SelectItem>
                <SelectItem value="transfer">Transfer</SelectItem>
                <SelectItem value="activity">Activity</SelectItem>
                <SelectItem value="meal">Meal</SelectItem>
                <SelectItem value="flight">Flight</SelectItem>
                <SelectItem value="language_excursions">Language Excursions</SelectItem>
                <SelectItem value="conference">Conference</SelectItem>
                <SelectItem value="tour">Tour</SelectItem>
                <SelectItem value="insurance">Insurance</SelectItem>
                <SelectItem value="supplier">Supplier</SelectItem>
                <SelectItem value="universal_fee">Universal Fee</SelectItem>
                {extraCategories.map((c: any) => (
                  <SelectItem key={c.id} value={`cat:${c.id}`}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.item_type && <p className="text-sm text-destructive">{errors.item_type}</p>}
          </div>

          {/* Accommodation-specific fields */}
          {isAccommodation && (
            <>
              <div className="space-y-2">
                <Label htmlFor="accommodation_type">Accommodation Type</Label>
                <Select
                  value={formData.accommodation_type}
                  onValueChange={(value) => {
                    setFormData({ ...formData, accommodation_type: value, supplier: "", product_id: "", product_name: "", location: "" });
                    // Reset room rate state when changing accommodation type
                    setSelectedSupplierId(null);
                    setSelectedRoomRateId(null);
                    setSelectedLocation("");
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select accommodation type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hotels">Hotels</SelectItem>
                    <SelectItem value="guesthouses">Guesthouses</SelectItem>
                    <SelectItem value="safari_lodges">Safari Lodges</SelectItem>
                    <SelectItem value="beach_lodges">Beach Lodges</SelectItem>
                    <SelectItem value="hostels">Hostels</SelectItem>
                    <SelectItem value="retreat_lodges">Retreat Lodges</SelectItem>
                    <SelectItem value="villas">Villas</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <DatePickerField
                  label="Check In"
                  value={formData.check_in}
                  onChange={(date) => {
                    userHasPicked.current = true;
                    setFormData({ ...formData, check_in: date });
                  }}
                  defaultMonth={defaultDate}
                />

                <DatePickerField
                  label="Check Out"
                  value={formData.check_out}
                  onChange={(date) => {
                    userHasPicked.current = true;
                    setFormData({ ...formData, check_out: date });
                  }}
                  disabled={(date) => formData.check_in ? date < formData.check_in : false}
                  defaultMonth={formData.check_in ?? defaultDate}
                />
              </div>

              {/* Location dropdown for accommodation */}
              {formData.accommodation_type && inheritedCountry && (
                <p className="text-xs text-muted-foreground">
                  Country inherited from lead: {inheritedCountry}
                </p>
              )}

              {formData.accommodation_type && !inheritedCountry && (
                <div className="space-y-2">
                  <Label>Country</Label>
                  <Select
                    value={selectedLocation}
                    onValueChange={(value) => {
                      const loc = value === "__all__" ? "" : value;
                      setSelectedLocation(loc);
                      setFormData(prev => ({ ...prev, location: loc, supplier: "", product_id: "", product_name: "" }));
                      setSelectedSupplierId(null);
                      setSelectedRoomRateId(null);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select country to filter properties" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">All Countries</SelectItem>
                      {countryOptions?.map((c) => (
                        <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Accommodation Supplier Selection */}
              {formData.accommodation_type && (
                <div className="space-y-2">
                  <Label htmlFor="accommodation_supplier">Property</Label>
                  <Select
                    value={selectedSupplierId || ""}
                    onValueChange={(value) => {
                      const selectedSupplier = suppliers?.find(s => s.id === value);
                      setSelectedSupplierId(value);
                      setSelectedRoomRateId(null); // Reset room selection
                      setRoomCount(1); // Reset room count
                      setFormData({
                        ...formData,
                        supplier: selectedSupplier?.name || "",
                        product_name: selectedSupplier?.name || "",
                        location: selectedSupplier?.city || selectedSupplier?.territory || "",
                        description: (selectedSupplier as any)?.description || "",
                        inclusions: arrayToLines((selectedSupplier as any)?.default_inclusions),
                        exclusions: arrayToLines((selectedSupplier as any)?.default_exclusions),
                      });

                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select property" />
                    </SelectTrigger>
                    <SelectContent>
                      {suppliers?.map((supplier) => (
                        <SelectItem key={supplier.id} value={supplier.id}>
                          {supplier.name} {supplier.territory && `- ${supplier.territory}`}
                        </SelectItem>
                      ))}
                      {(!suppliers || suppliers.length === 0) && (
                        <SelectItem value="no-items" disabled>
                          No properties found
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Room Type Selection - show when supplier has room rates */}
              {!isVilla && selectedSupplierId && supplierRoomRates && supplierRoomRates.length > 0 && (
                <div className="space-y-2">
                  <Label htmlFor="room_type">Room Type</Label>
                  <Select
                    value={selectedRoomRateId || ""}
                    onValueChange={(value) => {
                      const selectedRate = supplierRoomRates.find(r => r.id === value);
                      setSelectedRoomRateId(value);
                      
                      // Auto-populate cost based on occupancy
                      const rate = getRateByOccupancy(selectedRate, occupancyType);
                      
                      setFormData(prev => ({
                        ...prev,
                        cost: rate > 0 ? rate.toString() : "0",
                      }));
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select room type" />
                    </SelectTrigger>
                    <SelectContent>
                      {supplierRoomRates.map((rate) => (
                        <SelectItem key={rate.id} value={rate.id}>
                          <div className="flex items-center gap-2">
                            <Bed className="h-4 w-4" />
                            <span>{rate.room_type}</span>
                            <span className="text-muted-foreground">
                              - {rate.currency} {rate.double_rate}/night
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Occupancy Type Selection - show when room is selected */}
              {!isVilla && selectedRoomRateId && (
                <div className="space-y-2">
                  <Label>Occupancy</Label>
                  <Select
                    value={occupancyType}
                    onValueChange={(value: "single" | "double" | "triple") => {
                      setOccupancyType(value);
                      const selectedRate = supplierRoomRates?.find(r => r.id === selectedRoomRateId);
                      const rate = getRateByOccupancy(selectedRate, value);
                      
                      setFormData(prev => ({
                        ...prev,
                        cost: rate > 0 ? rate.toString() : prev.cost,
                      }));
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem 
                        value="single" 
                        disabled={!supplierRoomRates?.find(r => r.id === selectedRoomRateId)?.single_rate}
                      >
                        Single Occupancy
                      </SelectItem>
                      <SelectItem value="double">Double/Twin Occupancy</SelectItem>
                      <SelectItem 
                        value="triple" 
                        disabled={!supplierRoomRates?.find(r => r.id === selectedRoomRateId)?.triple_rate}
                      >
                        Triple Occupancy
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Number of Rooms Field - show when room rate is selected */}
              {!isVilla && selectedRoomRateId && (
                <div className="space-y-2">
                  <Label>Number of Rooms</Label>
                  <Input
                    type="number"
                    min={1}
                    value={roomCount}
                    onChange={(e) => setRoomCount(Math.max(1, parseInt(e.target.value) || 1))}
                    className="w-24"
                  />
                </div>
              )}

              {/* Villa: Number of Guests (flat Private Hire rate) */}
              {isVilla && selectedSupplierId && (
                <div className="space-y-2">
                  <Label>Number of Guests *</Label>
                  <Input
                    type="number"
                    min={1}
                    value={guestCount}
                    onChange={(e) => setGuestCount(Math.max(1, parseInt(e.target.value) || 1))}
                    className="w-24"
                  />
                  <p className="text-xs text-muted-foreground">
                    Flat private-hire rate — price does not change with guest count.
                  </p>
                </div>
              )}

              {/* Show nightly calculation when room rate is selected */}
              {selectedRoomRateId && numberOfNights > 0 && (
                <div className="rounded-md bg-muted p-3 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">
                      {isVilla
                        ? `${numberOfNights} night${numberOfNights > 1 ? 's' : ''} × ${supplierRoomRates?.find(r => r.id === selectedRoomRateId)?.currency || 'USD'} ${formData.cost}/night`
                        : `${numberOfNights} night${numberOfNights > 1 ? 's' : ''} × ${roomCount} room${roomCount > 1 ? 's' : ''} × ${supplierRoomRates?.find(r => r.id === selectedRoomRateId)?.currency || 'USD'} ${formData.cost}/night`}
                    </span>
                    <span className="font-medium">
                      = {supplierRoomRates?.find(r => r.id === selectedRoomRateId)?.currency || 'USD'} {(parseFloat(formData.cost || '0') * numberOfNights * (isVilla ? 1 : roomCount)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Universal fee: kind picker + S&T staff categories */}
          {isUniversalFee && (
            <>
              <div className="space-y-2">
                <Label htmlFor="universal_fee_kind">Fee Kind *</Label>
                <Select
                  value={formData.universal_fee_kind}
                  onValueChange={(value: "portage" | "guiding" | "assistant_guide" | "st" | "staff_hotel" | "chef" | "fejo" | "custom") =>
                    setFormData({
                      ...formData,
                      universal_fee_kind: value,
                      cost: value === "fejo" && !formData.cost ? "4600" : formData.cost,
                      staff_categories:
                        value === "st"
                          ? formData.staff_categories
                          : value === "staff_hotel"
                          ? formData.staff_categories.slice(0, 1)
                          : [],
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select fee kind" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="portage">Portage Fees</SelectItem>
                    <SelectItem value="guiding">Guiding Fees</SelectItem>
                    <SelectItem value="assistant_guide">Assistant Guide Fees</SelectItem>
                    <SelectItem value="st">S&amp;T (Subsistence &amp; Transport)</SelectItem>
                    <SelectItem value="staff_hotel">Staff Hotel</SelectItem>
                    <SelectItem value="chef">Private Chef</SelectItem>
                    <SelectItem value="fejo">FEJO Consulting Fee</SelectItem>
                    <SelectItem value="custom">Custom Fee</SelectItem>
                  </SelectContent>
                </Select>
                {errors.universal_fee_kind && (
                  <p className="text-sm text-destructive">{errors.universal_fee_kind}</p>
                )}
              </div>

              {universalKind === "custom" && (
                <div className="space-y-2">
                  <Label htmlFor="custom_fee_label">Fee Name *</Label>
                  <Input
                    id="custom_fee_label"
                    value={formData.custom_fee_label}
                    onChange={(e) => setFormData({ ...formData, custom_fee_label: e.target.value })}
                    placeholder="e.g. Cape Town Int'l Jazz Festival tickets"
                    maxLength={200}
                  />
                  {errors.custom_fee_label && (
                    <p className="text-sm text-destructive">{errors.custom_fee_label}</p>
                  )}
                </div>
              )}

              {universalKind === "st" && (
                <div className="space-y-2">
                  <Label>Staff Categories *</Label>
                  <div className="flex flex-wrap gap-4 rounded-md border p-3">
                    {(["driver", "guide", "assistant_guide", "other"] as const).map((cat) => {
                      const checked = formData.staff_categories.includes(cat);
                      return (
                        <label key={cat} className="flex items-center gap-2 text-sm capitalize cursor-pointer">
                          <Checkbox
                            checked={checked}
                            onCheckedChange={(v) => {
                              const next = v
                                ? [...formData.staff_categories, cat]
                                : formData.staff_categories.filter((c) => c !== cat);
                              setFormData({ ...formData, staff_categories: next });
                            }}
                          />
                          {cat === "assistant_guide" ? "Assistant Guide" : cat}
                        </label>
                      );
                    })}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Total = cost × days × number of staff categories selected.
                  </p>
                  {errors.staff_categories && (
                    <p className="text-sm text-destructive">{errors.staff_categories}</p>
                  )}
                </div>
              )}

              {universalKind === "staff_hotel" && (
                <div className="space-y-2">
                  <Label>Staff Categories &amp; Per-Night Cost *</Label>
                  <div className="space-y-2 rounded-md border p-3">
                    {(["driver", "guide", "other"] as const).map((cat) => {
                      const checked = formData.staff_categories.includes(cat);
                      return (
                        <div key={cat} className="flex items-center gap-3">
                          <label className="flex items-center gap-2 text-sm capitalize cursor-pointer w-24">
                            <Checkbox
                              checked={checked}
                              onCheckedChange={(v) => {
                                const next = v
                                  ? [...formData.staff_categories, cat]
                                  : formData.staff_categories.filter((c) => c !== cat);
                                setFormData({ ...formData, staff_categories: next });
                                if (!v) {
                                  setStaffHotelCosts((prev) => {
                                    const { [cat]: _, ...rest } = prev;
                                    return rest;
                                  });
                                }
                              }}
                            />
                            {cat}
                          </label>
                          <Input
                            type="number"
                            step="0.01"
                            placeholder="Cost per night"
                            disabled={!checked}
                            value={staffHotelCosts[cat] || ""}
                            onChange={(e) =>
                              setStaffHotelCosts((prev) => ({ ...prev, [cat]: e.target.value }))
                            }
                            className="flex-1"
                          />
                        </div>
                      );
                    })}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Each selected category becomes its own line item. Number of days below is shared across all of them.
                  </p>
                  {errors.staff_categories && (
                    <p className="text-sm text-destructive">{errors.staff_categories}</p>
                  )}
                </div>
              )}

              {!isInfrastructureFeeKind && (
                <DatePickerField
                  label="Date"
                  value={formData.date}
                  onChange={(date) => {
                    userHasPicked.current = true;
                    setFormData({ ...formData, date });
                  }}
                  defaultMonth={defaultDate}
                />
              )}
              {isInfrastructureFeeKind && (
                <p className="text-xs text-muted-foreground -mt-2">
                  No date required — this fee runs for the duration of the programme and will appear under <strong>Infrastructure Fees</strong>.
                </p>
              )}

              {isMultiDayService && (
                <div className="grid grid-cols-2 gap-4">
                  <DatePickerField
                    label="Start Date *"
                    value={formData.service_start}
                    onChange={(date) => {
                      userHasPicked.current = true;
                      setFormData({ ...formData, service_start: date });
                    }}
                    defaultMonth={formData.service_start ?? defaultDate}
                  />
                  <DatePickerField
                    label="End Date *"
                    value={formData.service_end}
                    onChange={(date) => {
                      userHasPicked.current = true;
                      setFormData({ ...formData, service_end: date });
                    }}
                    disabled={(date) => formData.service_start ? date < formData.service_start : false}
                    defaultMonth={formData.service_start ?? defaultDate}
                  />
                  {errors.service_start && (
                    <p className="text-sm text-destructive col-span-2">{errors.service_start}</p>
                  )}
                  {errors.service_end && (
                    <p className="text-sm text-destructive col-span-2">{errors.service_end}</p>
                  )}
                </div>
              )}

              <div className={universalKind === "staff_hotel" ? "" : "grid grid-cols-2 gap-4"}>
                {universalKind !== "staff_hotel" && (
                  <div className="space-y-2">
                    <Label htmlFor="uf_cost">
                      {isMultiDayService
                        ? (universalKind === "chef" ? "Cost per day" : "Cost per person per day")
                        : isDayBased ? "Cost per day" : "Cost per unit"}
                    </Label>
                    <Input
                      id="uf_cost"
                      type="number"
                      step="0.01"
                      value={formData.cost}
                      onChange={(e) => setFormData({ ...formData, cost: e.target.value })}
                      placeholder="0.00"
                    />
                    {errors.cost && <p className="text-sm text-destructive">{errors.cost}</p>}
                  </div>
                )}
                {!(isMultiDayService && universalKind === "chef") && (
                <div className="space-y-2">
                  <Label htmlFor="uf_qty">
                    {isMultiDayService
                      ? `Number of ${universalKind === "chef" ? "chefs" : universalKind === "guiding" ? "guides" : universalKind === "assistant_guide" ? "assistant guides" : "staff"}`
                      : isDayBased
                        ? "Number of days"
                        : "Quantity"}
                  </Label>
                  <Input
                    id="uf_qty"
                    type="number"
                    min="1"
                    value={formData.quantity}
                    onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                    placeholder="1"
                  />
                  {errors.quantity && <p className="text-sm text-destructive">{errors.quantity}</p>}
                </div>
                )}
              </div>

              {isMultiDayService && formData.service_start && formData.service_end && (() => {
                const days = Math.max(1, differenceInDays(formData.service_end, formData.service_start) + 1);
                const cost = parseFloat(formData.cost) || 0;
                const isChef = universalKind === "chef";
                const qty = isChef ? 1 : (parseInt(formData.quantity) || 1);
                const staffMult = universalKind === "st" ? Math.max(1, formData.staff_categories.length) : 1;
                const total = cost * days * qty * staffMult;
                return (
                  <div className="p-3 bg-muted/50 rounded-lg text-sm">
                    <span className="font-medium">Total: </span>
                    {cost.toLocaleString()} × {days} {days === 1 ? "day" : "days"}
                    {!isChef ? ` × ${qty}` : ""}
                    {universalKind === "st" && staffMult > 1 ? ` × ${staffMult} categories` : ""}
                    <span className="font-semibold"> = {total.toLocaleString()}</span>
                  </div>
                );
              })()}
            </>
          )}

          {/* Product dropdown for non-accommodation, non-universal-fee item types */}
          {formData.item_type !== "accommodation" && !isUniversalFee && (
            <div className="space-y-4">
              {/* Location dropdown for non-accommodation */}
              {inheritedCountry ? (
                <p className="text-xs text-muted-foreground">
                  Country inherited from lead: {inheritedCountry}
                </p>
              ) : (
              <div className="space-y-2">
                <Label>Country</Label>
                <Select
                  value={selectedLocation}
                  onValueChange={(value) => {
                    const loc = value === "__all__" ? "" : value;
                    setSelectedLocation(loc);
                    setFormData(prev => ({ ...prev, location: loc, product_id: "", product_name: "", supplier: "" }));
                  }}
                  disabled={isSupplierType && !!formData.supplier}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={isSupplierType ? "Auto-fills from selected supplier" : "Select country to filter options"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All Countries</SelectItem>
                    {countryOptions?.map((c) => (
                      <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {isSupplierType && formData.supplier && (
                  <p className="text-xs text-muted-foreground">
                    Locked to the selected supplier's country.
                  </p>
                )}
              </div>
              )}
              <div className="flex items-center justify-between">
                <Label htmlFor="product">
                  {isSupplierType ? "Supplier" :
                   formData.item_type === "tour" ? "Tour" :
                   formData.item_type === "activity" ? "Activity" :
                   formData.item_type === "meal" ? "Restaurant" :
                   formData.item_type === "transfer" ? "Transfer Service" :
                   formData.item_type === "flight" ? "Flight" :
                   formData.item_type === "language_excursions" ? "Language Excursion" : formData.item_type === "conference" ? "Conference" :
                   "Product"}
                </Label>
                <div className="flex gap-2">
                  {!isSupplierType && (
                    <AddProductDialogInline 
                      onProductCreated={(id) => setFormData({ ...formData, product_id: id })}
                      categoryId={categories?.find(c => 
                        (formData.item_type === 'tour' && c.name === 'Tour') ||
                        (formData.item_type === 'activity' && c.name === 'Activity') ||
                        (formData.item_type === 'meal' && c.name === 'Restaurants') ||
                        (formData.item_type === 'transfer' && c.name === 'Transport')
                      )?.id}
                    />
                  )}
                  <AddSupplierDialog categories={categories || []} onSuccess={() => queryClient.invalidateQueries({ queryKey: ["suppliers"] })} />
                  {!isSupplierType && (
                    <AddCategoryDialogInline onCategoryCreated={() => queryClient.invalidateQueries({ queryKey: ["supplier-categories"] })} />
                  )}
                </div>
              </div>
              {isSupplierType ? (
                <Select
                  value={formData.supplier ? `supplier-by-name-${formData.supplier}` : ""}
                  onValueChange={(value) => {
                    const supplierId = value.replace('supplier-id-', '');
                    const selectedSupplier = allSuppliers?.find(s => s.id === supplierId);
                    if (!selectedSupplier) return;
                    const territory = selectedSupplier.territory || '';
                    setSelectedLocation(territory);
                    setFormData(prev => ({
                      ...prev,
                      product_id: '',
                      product_name: selectedSupplier.product_name || selectedSupplier.name || '',
                      supplier: selectedSupplier.name || '',
                      description: selectedSupplier.description || selectedSupplier.name || '',
                      inclusions: arrayToLines((selectedSupplier as any).default_inclusions),
                      exclusions: arrayToLines((selectedSupplier as any).default_exclusions),
                      location: territory,

                    }));
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select supplier">
                      {formData.supplier || undefined}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {allSuppliers?.map((supplier) => (
                      <SelectItem key={`supplier-id-${supplier.id}`} value={`supplier-id-${supplier.id}`}>
                        {supplier.name} {supplier.territory && `- ${supplier.territory}`}
                      </SelectItem>
                    ))}
                    {(!allSuppliers || allSuppliers.length === 0) && (
                      <SelectItem value="no-suppliers" disabled>
                        No suppliers found
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              ) : (
              <Select
                value={formData.product_id}
                onValueChange={(value) => {
                  // Check if selected value is a product or a supplier (prefixed with 'supplier-')
                  if (value.startsWith('supplier-')) {
                    const supplierId = value.replace('supplier-', '');
                    const selectedSupplier = suppliers?.find(s => s.id === supplierId);
                    setFormData({ 
                      ...formData, 
                      product_id: '', // No product selected
                      product_name: selectedSupplier?.product_name || selectedSupplier?.name || '',
                      supplier: selectedSupplier?.name || '',
                      description: selectedSupplier?.description || selectedSupplier?.name || '',
                      inclusions: arrayToLines((selectedSupplier as any)?.default_inclusions),
                      exclusions: arrayToLines((selectedSupplier as any)?.default_exclusions),
                      location: (selectedSupplier as any)?.city || selectedSupplier?.territory || '' // Prefer city, fall back to country

                    });
                  } else {
                    const selectedProduct = products?.find(p => p.id === value);
                    const supplierName = selectedProduct?.suppliers?.name || '';
                    const productName = selectedProduct?.product_name || '';
                    
                    // Store pricing info for dynamic recalculation when quantity changes
                    const pricingInfo = selectedProduct ? {
                      tour_type: selectedProduct.tour_type || null,
                      base_price: selectedProduct.base_price || null,
                      pricing_tiers: selectedProduct.pricing_tiers as unknown as PricingTier[] || null
                    } : null;
                    setSelectedProductPricing(pricingInfo);
                    
                    // Handle time slots from product
                    const productTimeSlots = (selectedProduct as any)?.time_slots as Array<{label: string; start: string; end: string}> || [];
                    setAvailableTimeSlots(productTimeSlots);
                    // Auto-select if only one time slot
                    if (productTimeSlots.length === 1) {
                      setSelectedTimeSlotIndex(0);
                    } else {
                      setSelectedTimeSlotIndex(null);
                    }
                    // Reset custom time when product changes
                    setUseCustomTime(false);
                    setCustomStartTime("");
                    setCustomEndTime("");
                    
                    // Set initial quantity to lead's pax count
                    const initialQuantity = paxCount || 1;
                    
                    // Calculate per-person cost based on tier
                    const perPersonCost = pricingInfo ? getPerPersonRate(pricingInfo, initialQuantity) : 0;
                    
                    setFormData({ 
                      ...formData, 
                      product_id: value, 
                      product_name: productName,
                      supplier: supplierName,
                      description: selectedProduct?.description || productName,
                      inclusions: arrayToLines((selectedProduct as any)?.default_inclusions),
                      exclusions: arrayToLines((selectedProduct as any)?.default_exclusions),

                      location: selectedProduct?.city || (selectedProduct as any)?.suppliers?.city || (selectedProduct as any)?.suppliers?.territory || '', // Prefer city, fall back to supplier city/country
                      quantity: initialQuantity.toString(),
                      cost: perPersonCost > 0 ? perPersonCost.toString() : formData.cost
                    });
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder={
                    formData.item_type === "meal" ? "Select restaurant" : 
                    `Select ${formData.item_type}`
                  } />
                </SelectTrigger>
                <SelectContent>
                  {/* Show products first */}
                  {products?.map((product) => (
                    <SelectItem key={product.id} value={product.id}>
                      {product.product_name} {product.city && `- ${product.city}`}
                    </SelectItem>
                  ))}
                  {/* Show suppliers not already covered by products */}
                  {suppliers
                    ?.filter((supplier) => {
                      // Don't show supplier if a product already contains their name
                      const supplierNameLower = supplier.name.toLowerCase();
                      return !products?.some((product) => 
                        product.product_name.toLowerCase().includes(supplierNameLower)
                      );
                    })
                    .map((supplier) => (
                      <SelectItem key={`supplier-${supplier.id}`} value={`supplier-${supplier.id}`}>
                        {supplier.name} {supplier.territory && `- ${supplier.territory}`}
                      </SelectItem>
                    ))}
                  {(!products || products.length === 0) && (!suppliers || suppliers.length === 0) && (
                    <SelectItem value="no-items" disabled>
                      No {formData.item_type}s found
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
              )}

              {/* Time Slot Selector - show when product has predefined time slots and custom time not active */}
              {availableTimeSlots.length > 1 && !useCustomTime && (
                <div className="space-y-2 mt-4">
                  <Label htmlFor="time_slot">Time Slot *</Label>
                  <Select
                    value={selectedTimeSlotIndex?.toString() ?? ""}
                    onValueChange={(value) => setSelectedTimeSlotIndex(parseInt(value))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select time slot" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableTimeSlots.map((slot, idx) => (
                        <SelectItem key={idx} value={idx.toString()}>
                          {slot.label}: {slot.start.replace(':', 'h')} - {slot.end.replace(':', 'h')}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {/* Show selected time slot for single-slot products (when custom time not active) */}
              {availableTimeSlots.length === 1 && !useCustomTime && (
                <div className="mt-2 text-sm text-muted-foreground flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  <span>{availableTimeSlots[0].label}: {availableTimeSlots[0].start.replace(':', 'h')} - {availableTimeSlots[0].end.replace(':', 'h')}</span>
                </div>
              )}

              {/* Custom Time Section — shown for all non-accommodation, non-insurance items with a product selected */}
              {formData.item_type !== "insurance" && (formData.product_id || formData.product_name) && (
                <div className="mt-4 space-y-3">
                  {availableTimeSlots.length > 0 ? (
                    /* Has predefined slots — show toggle */
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="use_custom_time"
                        checked={useCustomTime}
                        onCheckedChange={(checked) => {
                          setUseCustomTime(!!checked);
                          if (!checked) {
                            setCustomStartTime("");
                            setCustomEndTime("");
                          }
                        }}
                      />
                      <label htmlFor="use_custom_time" className="text-sm font-medium cursor-pointer">
                        Use custom time instead
                      </label>
                    </div>
                  ) : (
                    /* No predefined slots — always show time inputs labelled as optional */
                    <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      <span>Time (optional)</span>
                    </div>
                  )}

                  {/* Time inputs — visible when custom time is active OR when no predefined slots */}
                  {(useCustomTime || availableTimeSlots.length === 0) && (
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <Label htmlFor="custom_start_time" className="text-xs text-muted-foreground">Start Time</Label>
                        <Input
                          id="custom_start_time"
                          type="time"
                          value={customStartTime}
                          onChange={(e) => setCustomStartTime(e.target.value)}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor="custom_end_time" className="text-xs text-muted-foreground">End Time</Label>
                        <Input
                          id="custom_end_time"
                          type="time"
                          value={customEndTime}
                          onChange={(e) => setCustomEndTime(e.target.value)}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Supplier field - read-only when product is selected (non-accommodation only) */}
          {formData.item_type !== "accommodation" && !isUniversalFee && (
            <div className="space-y-2">
              <Label htmlFor="supplier">Supplier</Label>
              <Input
                id="supplier"
                value={formData.supplier}
                readOnly
                disabled={!!formData.product_id}
                className={formData.product_id ? "bg-muted" : ""}
                onChange={(e) => !formData.product_id && setFormData({ ...formData, supplier: e.target.value })}
              />
            </div>
          )}

          {/* General date field for non-accommodation items (exclude insurance) */}
          {!isAccommodation && !isUniversalFee && formData.item_type !== "insurance" && (
            <DatePickerField
              label="Date"
              value={formData.date}
              onChange={(date) => {
                userHasPicked.current = true;
                setFormData({ ...formData, date });
              }}
              defaultMonth={defaultDate}
            />
          )}
          {!isAccommodation && !isUniversalFee && formData.item_type !== "insurance" && (
            <SessionScheduleField startDate={formData.date} value={schedule} onChange={setSchedule} />
          )}

          {/* Only show Cost per Unit and Quantity for non-accommodation, non-universal-fee items */}
          {formData.item_type !== "accommodation" && !isUniversalFee && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="cost">Cost per Unit</Label>
                <Input
                  id="cost"
                  type="number"
                  step="0.01"
                  value={formData.cost}
                  onChange={(e) => setFormData({ ...formData, cost: e.target.value })}
                  placeholder="0.00"
                />
                {errors.cost && <p className="text-sm text-destructive">{errors.cost}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="quantity">Quantity</Label>
                <Input
                  id="quantity"
                  type="number"
                  value={formData.quantity}
                  onChange={(e) => {
                    const newQuantity = parseInt(e.target.value) || 1;
                    let newCost = formData.cost;
                    
                    // Recalculate per-person cost if we have product pricing info
                    if (selectedProductPricing) {
                      const perPersonRate = getPerPersonRate(selectedProductPricing, newQuantity);
                      if (perPersonRate > 0) {
                        newCost = perPersonRate.toString();
                      }
                    }
                    
                    setFormData({ 
                      ...formData, 
                      quantity: e.target.value,
                      cost: newCost 
                    });
                  }}
                  placeholder="1"
                />
                {errors.quantity && <p className="text-sm text-destructive">{errors.quantity}</p>}
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Item description"
              rows={3}
            />
            {errors.description && <p className="text-sm text-destructive">{errors.description}</p>}
          </div>

          <ItemInclusionsExclusionsFields
            inclusions={formData.inclusions}
            exclusions={formData.exclusions}
            onInclusionsChange={(v) => setFormData({ ...formData, inclusions: v })}
            onExclusionsChange={(v) => setFormData({ ...formData, exclusions: v })}
            onSaveToSource={
              formData.product_id || formData.supplier ? handleSaveDefaults : undefined
            }
            saveToSourceLabel={
              formData.product_id ? "Save to this experience" : "Save to this supplier"
            }
            isSavingToSource={savingDefaults}
          />


          <div className="space-y-2">
            <Label htmlFor="notes">Internal Notes</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Internal notes (not visible to client)"
              rows={2}
            />
            {errors.notes && <p className="text-sm text-destructive">{errors.notes}</p>}
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="submit">Add Item</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>

    <Button 
      className="gap-2" 
      variant="outline"
      disabled={isCancelled || isReadOnly}
      onClick={() => setCustomItemOpen(true)}
    >
      <Sparkles className="h-4 w-4" />
      Create Custom Item
    </Button>

    <AddCustomItemDialog
    open={customItemOpen}
    onOpenChange={setCustomItemOpen}
    leadId={leadId}
    session={session}
    leadStartDate={leadStartDate}
  />
</>
  );
};
